import { useCallback, useEffect, useState } from 'react'
import { AUDIT_ACTION_OPTIONS, auditLogApi, type AuditLogEntry } from '@/lib/api/audit-log'

function formatAction(action: string): string {
  return action.replace(/_/g, ' ')
}

function formatDetails(entry: AuditLogEntry): string {
  const parts: string[] = []
  if (entry.details?.category) parts.push(entry.details.category)
  if (entry.details?.reason) parts.push(entry.details.reason)
  if (entry.details?.archivedBy) parts.push(`by ${entry.details.archivedBy}`)
  if (entry.entityType) parts.push(entry.entityType)
  return parts.join(' · ') || '—'
}

export function AuditLogPanel() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [actionFilter, setActionFilter] = useState('')
  const [loading, setLoading] = useState(true)

  const loadEntries = useCallback(() => {
    setLoading(true)
    auditLogApi
      .listAuditLog({ action: actionFilter || undefined, perPage: 25 })
      .then((result) => {
        setEntries(result.items)
        setTotal(result.total)
      })
      .catch(() => {
        setEntries([])
        setTotal(0)
      })
      .finally(() => setLoading(false))
  }, [actionFilter])

  useEffect(() => {
    loadEntries()
  }, [loadEntries])

  return (
    <section className="rounded-lg border border-border bg-card p-4 md:col-span-2" data-testid="settings-audit-log">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold">Audit Log</h2>
          <p className="text-xs text-muted-foreground">Recent security-relevant actions</p>
        </div>
        <select
          className="min-h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm sm:w-56"
          value={actionFilter}
          onChange={(event) => setActionFilter(event.target.value)}
          data-testid="settings-audit-action-filter"
        >
          {AUDIT_ACTION_OPTIONS.map((option) => (
            <option key={option.value || 'all'} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading audit log…</p>
      ) : entries.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="settings-audit-empty">
          No audit entries yet.
        </p>
      ) : (
        <>
          <p className="mt-3 text-xs text-muted-foreground" data-testid="settings-audit-total">
            Showing {entries.length} of {total}
          </p>

          <div className="mt-3 hidden overflow-x-auto md:block">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3">Time</th>
                  <th className="py-2 pr-3">Action</th>
                  <th className="py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-b border-border/60 last:border-0"
                    data-testid={`settings-audit-row-${entry.id}`}
                  >
                    <td className="py-2 pr-3 whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(entry.createdAt).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3 capitalize">{formatAction(entry.action)}</td>
                    <td className="py-2 text-muted-foreground">{formatDetails(entry)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="mt-3 space-y-2 md:hidden">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="rounded-md border border-border px-3 py-2"
                data-testid={`settings-audit-card-${entry.id}`}
              >
                <p className="text-xs text-muted-foreground">{new Date(entry.createdAt).toLocaleString()}</p>
                <p className="mt-1 text-sm font-medium capitalize">{formatAction(entry.action)}</p>
                <p className="mt-1 text-xs text-muted-foreground">{formatDetails(entry)}</p>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
